import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./ConfirmRecalculateTopicsModal.module.scss";
import { Button } from "src/components";
import { Cross, Loader } from "src/assets/icons";
import { useModal, useKeyboardListener, useGlobalPreloader } from "src/hooks";

type Props = {
  onConfirm: () => Promise<void>;
  onCancel: () => void;
};

export const ConfirmRecalculateTopicsModal: FC<Props> = ({
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);

  const { showGlobalPreloader, hideGlobalPreloader } = useGlobalPreloader();
  

  const handleClose = () => {
    if (isLoading) return;
    onCancel();
    closeModal("confirm-recalculate-topics");
  };

  useKeyboardListener({
    key: "Escape",
    type: "keydown",
    callback: handleClose,
  });

  const handleConfirm = async () => {
    setIsLoading(true);
    showGlobalPreloader(t("component.modal.loader_message"));
    try {
      closeModal("confirm-recalculate-topics");
      await onConfirm();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      hideGlobalPreloader();
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>
          <span>{t("component.modal.confirm_recalculate_topics.title")}</span>
        </div>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          className={styles.closeButton}
          buttonSize="small"
          buttonStyle="outlined"
        >
          <Cross />
        </Button>
      </div>

      <div className={styles.content}>
        <p className={styles.description}>
          {t("component.modal.confirm_recalculate_topics.description")}
        </p>
      </div>

      <div className={styles.buttonsWrapper}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          buttonStyle="outlined"
          className={styles.cancelButton}
        >
          {t("component.modal.confirm_recalculate_topics.button.cancel")}
        </Button>

        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          className={styles.confirmButton}
        >
          {isLoading ? (
            <Loader className={styles.loader} />
          ) : (
            t("component.modal.confirm_recalculate_topics.button.proceed")
          )}
        </Button>
      </div>
    </div>
  );
};

